import path from "node:path";
import { fileURLToPath } from "node:url";
import { InferenceSession, Tensor } from "onnxruntime-node";
import { z } from "zod";
import { zodObjectWithTelemetry } from "@/telemetry/helpers/zod";
import { VADBase, type VADJob } from "../../base";

// While the received PCM chunks are 10ms (160 chunks) and so the rolling window already includes context
// from all the examples found on the Silero VAD codebase, it seems that the Silero VAD model has been trained
// on 64 chunks of context plus 512 chunks of fresh audio data (= 576 chunks).
const WINDOW_SIZE = 64 + 512;

// 16kHz sample rate
const SAMPLE_RATE = 16_000;

// Config
export const sileroVADConfig = zodObjectWithTelemetry({
  schema: z.object({
    provider: z.literal("silero"),
  }),
  toTelemetry: (config) => config,
});

// Model
export class SileroVAD extends VADBase<typeof sileroVADConfig.schema> {
  readonly #jobsStates: Map<string, Tensor> = new Map();
  readonly #jobsSessions: Map<string, InferenceSession> = new Map();
  readonly #jobsContextWindows: Map<string, Float32Array> = new Map();

  constructor(config: z.input<typeof sileroVADConfig.schema>) {
    super(sileroVADConfig.schema, config);
  }

  async detect(): Promise<VADJob> {
    // Create a new generation job
    const job = this.createGenerateJob();

    // Create the ONNX inference session
    const currentDir = path.dirname(fileURLToPath(import.meta.url));
    const modelPath = path.join(
      currentDir,
      "..",
      "models",
      "vad",
      "providers",
      "silero",
      "model.onnx",
    );
    const session = await InferenceSession.create(modelPath);
    this.#jobsSessions.set(job.id, session);

    // Initialize the state tensor
    const state = new Tensor("float32", new Float32Array(2 * 1 * 128), [2, 1, 128]);
    this.#jobsStates.set(job.id, state);

    // Clean up the session on job cancellation
    job._abortController.signal.addEventListener("abort", async () => {
      await session.release();
      this.#jobsStates.delete(job.id);
      this.#jobsSessions.delete(job.id);
      this.#jobsContextWindows.delete(job.id);
    });

    return job;
  }

  protected async onVoiceInput(job: VADJob, pcm: Int16Array) {
    const session = this.#jobsSessions.get(job.id);
    if (!session) throw new Error("Session not found. Shouldn't happen.");
    try {
      const score = await this.#runInference(job, pcm);
      job.stream.push({ type: "result", chunk: pcm, score });
    } catch (error) {
      console.error("  Error in onVoiceInput:", error);
      throw error;
    }
  }

  async #runInference(job: VADJob, pcm: Int16Array): Promise<number> {
    // Convert incoming PCM audio to Float32
    const newSamples = this.#int16ToFloat32(pcm);

    // Compute the new context window
    // This a rolling buffer, we basically do [...oldSamples, ...newSamples].slice(-WINDOW_SIZE)
    const contextWindow = this.#jobsContextWindows.get(job.id) ?? new Float32Array(WINDOW_SIZE);
    const newSamplesStartAt = WINDOW_SIZE - newSamples.length;
    const oldSamples = contextWindow.slice(-newSamplesStartAt);
    contextWindow.set(oldSamples, 0);
    contextWindow.set(newSamples, newSamplesStartAt);
    this.#jobsContextWindows.set(job.id, contextWindow);

    // Run ONNX model inference
    const session = this.#jobsSessions.get(job.id);
    if (!session) throw new Error("Session not found. Shouldn't happen.");
    const input = new Tensor("float32", contextWindow, [1, contextWindow.length]);
    const state = this.#jobsStates.get(job.id);
    if (!state) throw new Error("State not found. Shouldn't happen.");
    const sr = new Tensor("int64", BigInt64Array.from([BigInt(SAMPLE_RATE)]));
    const result = await session.run({ input, state, sr });
    if (result.stateN) this.#jobsStates.set(job.id, result.stateN);

    // Retrieve and return the voice activity score
    const score = Number(result.output?.data?.[0]) ?? 0;
    return score;
  }

  #int16ToFloat32(src: Int16Array, dst?: Float32Array) {
    const out = dst ?? new Float32Array(src.length);
    for (let i = 0; i < src.length; ++i) out[i] = (src[i] as number) / 32_768;
    return out;
  }
}
